import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  CircularProgress,
  Grid,
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { CloseRounded as CloseIcon } from '@material-ui/icons'
import { useTheme } from '@material-ui/core/styles'

import Typography from '../../atoms/display/typography'
import TextField from '../../atoms/inputs/textfield'

import api from '../../../services/api'
import {
  addShortcut,
  addedShortcut,
  shortcutError,
  updateShortcut,
  updatedShortcut,
  resetShortcutStatus,
} from '../../../redux/shortcuts/actions'

import { isArray, objLength } from '../../../utils/customMethods'

const Shortcut = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()

  const shortcuts = useSelector((state) => state.shortcuts)
  const prevValues = shortcuts?.shortcuts?.find(
    (e) => e.id === props.shortcutId
  )

  const [form, setForm] = useState({})
  const [formError, setFormError] = useState({})

  const shortcutMaxLength = 255
  const messageMaxLength = 1500

  const createShortcut = async (values) => {
    dispatch(addShortcut(values))
    try {
      const response = await api.post(`/flash-message`, { ...values })

      if (response.status === 200) {
        const message = response.data?.message
        values.id = response.data?.data?.id
        dispatch(addedShortcut(values, message))
        enqueueSnackbar(t('toasts.shortcutAdded'), {
          variant: 'success',
        })
        localStorage.setItem('undockCommand', 'updateShortcut')
        props?.open(false)
      } else {
        throw response.data
      }
    } catch (error) {
      const data = error?.response?.data
      const message = isArray(data?.message)
        ? data?.message
        : [{ value: 'shortcutError' }]

      message.forEach((e) =>
        enqueueSnackbar(t(`toasts.${e.value?.replace('.', '')}`), {
          variant: 'error',
        })
      )
      dispatch(
        shortcutError({ action: 'add_shortcut', message: message[0]?.value })
      )
    }
    dispatch(resetShortcutStatus('add_shortcut'))
  }

  const editShortcut = async (values) => {
    dispatch(updateShortcut(values))
    try {
      const response = await api.patch(
        `/flash-message${props?.shortcutId ? `?id=${props.shortcutId}` : ''}`,
        { ...values }
      )

      if (response.status === 200) {
        values.id = props.shortcutId

        dispatch(updatedShortcut(values))
        enqueueSnackbar(t('toasts.shortcutUpdated'), {
          variant: 'success',
        })
        props?.open(false)
      } else {
        throw response.data
      }
    } catch (error) {
      const data = error?.response?.data
      const message = isArray(data?.message)
        ? data?.message
        : [{ value: 'shortcutError' }]

      message.forEach((e) =>
        enqueueSnackbar(t(`toasts.${e.value?.replace('.', '')}`), {
          variant: 'error',
        })
      )

      dispatch(
        shortcutError({ action: 'update_shortcut', message: message[0]?.value })
      )
    }
    dispatch(resetShortcutStatus('update_shortcut'))
  }

  const handleSubmit = async () => {
    const shortcut = form?.short_name
    const message = form?.message
    let val = {}

    // Reset object
    setFormError({})

    if (props?.shortcutId) {
      if (shortcut !== prevValues?.short_name) val.short_name = shortcut
      if (message !== prevValues?.message) val.message = message
      if (objLength(val) > 0) {
        await editShortcut(val)
      }
    } else {
      if (shortcut && message && shortcut?.length >= 3) {
        val.short_name = shortcut
        val.message = message
        await createShortcut(val)
      } else {
        if (!shortcut) {
          setFormError((prev) => ({
            ...prev,
            short_name: 'shortNameIsRequired',
          }))
        }
        if (!message) {
          setFormError((prev) => ({
            ...prev,
            message: 'messageIsRequired',
          }))
        }
        if (shortcut?.length < 3) {
          setFormError((prev) => ({
            ...prev,
            short_name: 'shortNameMinLength',
          }))
        }
      }
    }
  }

  const inputHandle = (event) => {
    setForm((form) => ({
      ...form,
      [event?.target?.name]: event?.target?.value,
    }))
  }

  useEffect(() => {
    if (props?.shortcutId) {
      prevValues && setForm(prevValues)
    }
  }, [props?.shortcutId])

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.open ? true : false}
        onClose={() => props.open(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>{t('commons.shortcut')}</div>
            <div>
              <IconButton
                onClick={() => props.open(false)}
                style={{ color: theme.palette.primary.main }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container direction="row" spacing={2}>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h2">//</Typography>
                <div style={{ width: 20 }} />
                <TextField
                  id="field-shortcut"
                  label={t('fields.shortcut.label')}
                  placeholder={t('fields.shortcut.placeHolder')}
                  type="text"
                  color="primary"
                  variant="outlined"
                  name="short_name"
                  value={form.short_name ? form.short_name : ''}
                  onChange={inputHandle}
                  error={formError?.short_name ? true : false}
                  helperText={
                    formError?.short_name &&
                    t(`fields.shortcut.errors.${formError.short_name}`)
                  }
                  shrink={true}
                  fullWidth={true}
                  inputProps={{ maxLength: shortcutMaxLength }}
                />
              </div>
            </Grid>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 20,
                }}
              >
                <Typography>
                  {`${t('commons.maximumOf')} ${shortcutMaxLength} ${t(
                    'commons.characters'
                  )}`}
                </Typography>
                <Typography>
                  {`${form?.short_name?.length ?? 0}/${shortcutMaxLength}`}
                </Typography>
              </div>
            </Grid>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <TextField
                id="field-message"
                label={t('fields.message.label')}
                placeholder={t('fields.message.placeHolder')}
                type="text"
                color="primary"
                variant="outlined"
                name="message"
                multiline={true}
                rows={6}
                value={form.message ? form.message : ''}
                onChange={inputHandle}
                error={formError?.message ? true : false}
                helperText={
                  formError?.message &&
                  t(`fields.shortcut.errors.${formError.message}`)
                }
                shrink={true}
                fullWidth={true}
                inputProps={{ maxLength: messageMaxLength }}
              />
            </Grid>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>
                  {`${t('commons.maximumOf')} ${messageMaxLength} ${t(
                    'commons.characters'
                  )}`}
                </Typography>
                <Typography>
                  {`${form?.message?.length ?? 0}/${messageMaxLength}`}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            size="large"
            color="primary"
            onClick={() => props.open(false)}
          >
            {props.disagreeText ? props.disagreeText : t('buttons.cancel')}
          </Button>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={() => handleSubmit()}
            disabled={
              shortcuts?.status?.add_shortcut?.loading ||
              shortcuts?.status?.update_shortcut?.loading
            }
          >
            {shortcuts?.status?.add_shortcut?.loading ||
            shortcuts?.status?.update_shortcut?.loading ? (
              <CircularProgress size={25} color="primary" />
            ) : props?.agreeText ? (
              props.agreeText
            ) : (
              t('buttons.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default React.memo(Shortcut)
